import { useEffect, useRef, useCallback, useState } from "react";
import { useOrgPublicKey } from "~/hooks/useOrgEncryption";
import { usePassword } from "~/store/credentials";
import { base64ToUint8Array } from "~/hooks/useEncryption";
import {
    generatePerReportDek,
    wrapDekForUser,
    bufferToBase64,
} from "~/helpers/envelopeCrypto";
import { importRsaPublicKey, rsaOaepWrapDek } from "~/helpers/orgCrypto";
import { migrateEnvelope } from "~/services/gateways/orgEncryption.gateway";
import type { MigrateEnvelopeItem } from "~/domain/OrgEncryption";
import { encryptionWorker } from "~/services/workers/encryptionWorker.service";
import { encryptNgramsDeterministic } from "~/helpers/encryption.helper";

export interface LegacyMigrationProgress {
    total: number;
    processed: number;
    failed: number;
    isRunning: boolean;
}

interface LegacyWsItem {
    item_id: string;
    item_type: "report" | "folder" | "summary";
    encrypted_fields: Record<string, unknown>;
}

/**
 * Keys that should NOT be re-encrypted (plain metadata).
 * Must match NON_ENCRYPTED_KEYS from constants.ts
 */
const NON_ENCRYPTED_FIELD_KEYS = [
    "report_type", "organization_id", "user_id", "user_reports_id",
    "subject_age", "created_at", "modified_at",
    "summary_id", "report_id", "status",
];

function isEncryptedPayload(value: unknown): value is { encrypted: string; iv: string } {
    return (
        typeof value === "object" && value !== null &&
        "encrypted" in value && "iv" in value &&
        typeof (value as any).encrypted === "string" &&
        typeof (value as any).iv === "string"
    );
}

async function reEncryptField(
    value: unknown,
    rawUserKey: ArrayBuffer,
    dekBuffer: ArrayBuffer,
): Promise<unknown> {
    if (!isEncryptedPayload(value)) return value;
    const obj = value as Record<string, unknown>;
    const decrypted = await encryptionWorker.decrypt(
        { encrypted: obj.encrypted, iv: obj.iv },
        rawUserKey,
    );
    const reEncrypted = await encryptionWorker.encrypt(decrypted, dekBuffer);
    // Preserve extra keys beyond encrypted/iv (e.g. report_ids_used)
    const extraKeys = Object.keys(obj).filter(k => k !== "encrypted" && k !== "iv");
    if (extraKeys.length > 0) {
        const result = { ...(reEncrypted as Record<string, unknown>) };
        for (const key of extraKeys) result[key] = obj[key];
        return result;
    }
    return reEncrypted;
}

async function reEncryptFields(
    fields: Record<string, unknown>,
    rawUserKey: ArrayBuffer,
    dekBuffer: ArrayBuffer,
): Promise<Record<string, unknown>> {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(fields)) {
        result[key] = NON_ENCRYPTED_FIELD_KEYS.includes(key)
            ? value
            : await reEncryptField(value, rawUserKey, dekBuffer);
    }
    return result;
}

/**
 * Searchable fields per item type for HMAC generation.
 */
const REPORT_SEARCHABLE_FIELDS = [
    "report_name", "subject_name", "subject_mother_name",
    "subject_name_related", "subject_multiple_p_names",
    "subject_multiple_c_names", "report_search_args",
];

/**
 * Decrypts searchable fields from a legacy item to compute HMACs.
 */
async function decryptSearchableFields(
    fields: Record<string, unknown>,
    rawUserKey: ArrayBuffer,
    itemType: string,
): Promise<Record<string, unknown>> {
    const result: Record<string, unknown> = {};
    const searchableKeys = itemType === "folder"
        ? ["folder_name"]
        : itemType === "report"
            ? REPORT_SEARCHABLE_FIELDS
            : [];

    for (const key of searchableKeys) {
        const value = fields[key];
        if (value == null) continue;

        try {
            if (isEncryptedPayload(value)) {
                const decrypted = await encryptionWorker.decrypt(
                    { encrypted: value.encrypted, iv: value.iv },
                    rawUserKey,
                );
                result[key] = decrypted;
            } else if (typeof value === "string") {
                result[key] = value;
            }
        } catch {
            // Skip fields that fail to decrypt
        }
    }
    return result;
}

/**
 * Legacy migration via single bidirectional WebSocket.
 *
 * Follows the same safety design as useUserRewrapWs:
 * - WS is READ-ONLY for the database (server only streams legacy items)
 * - Client accumulates all re-encrypted items in memory
 * - Atomic submission via POST /reports/migrate-envelope at the end
 * - If disconnected mid-process, nothing is corrupted — migration restarts on next login
 *
 * Protocol:
 * 1. Server pushes pages of legacy items (all encrypted fields per item)
 * 2. Client decrypts with user key, re-encrypts with per-item DEK, stores in memory
 * 3. Client sends { type: "next" } for next page
 * 4. When has_more = false → client POSTs all items atomically
 * 5. Client sends { type: "done" } → server closes WS
 */
export function useLegacyMigrationWs() {
    const { data: orgPublicKey } = useOrgPublicKey();
    const secretKey = usePassword();
    const isReady = !!orgPublicKey && !!secretKey;

    // Stable refs for values that change between renders — breaks the
    // dependency chain that previously caused `start` to be recreated.
    const orgPublicKeyRef = useRef(orgPublicKey);
    const secretKeyRef = useRef(secretKey);
    useEffect(() => { orgPublicKeyRef.current = orgPublicKey; }, [orgPublicKey]);
    useEffect(() => { secretKeyRef.current = secretKey; }, [secretKey]);

    const [progress, setProgress] = useState<LegacyMigrationProgress>({
        total: 0, processed: 0, failed: 0, isRunning: false,
    });
    const [isComplete, setIsComplete] = useState(false);

    const wsRef = useRef<WebSocket | null>(null);
    const abortRef = useRef(false);
    const accumulatedRef = useRef<MigrateEnvelopeItem[]>([]);
    const processedIdsRef = useRef<Set<string>>(new Set());
    const rawUserKeyRef = useRef<ArrayBuffer | null>(null);
    const rsaPubKeyRef = useRef<CryptoKey | null>(null);
    const keyVersionRef = useRef<number>(0);

    /** Re-encrypt a page of items in memory (no DB writes). */
    const processPage = useCallback(async (items: LegacyWsItem[]): Promise<{ ok: number; fail: number }> => {
        const rawUserKey = rawUserKeyRef.current;
        const rsaPubKey = rsaPubKeyRef.current;
        const keyVersion = keyVersionRef.current;
        if (!rawUserKey || !rsaPubKey) return { ok: 0, fail: items.length };

        let ok = 0;
        let fail = 0;

        for (const item of items) {
            if (abortRef.current) break;
            if (processedIdsRef.current.has(item.item_id)) continue;
            try {
                const perItemDek = generatePerReportDek();
                const dekBuffer = perItemDek.buffer as ArrayBuffer;

                const reEncryptedFields = await reEncryptFields(
                    item.encrypted_fields, rawUserKey, dekBuffer,
                );

                const { user_wrapped_dek, user_dek_iv } = await wrapDekForUser(rawUserKey, perItemDek);
                const orgWrappedBuf = await rsaOaepWrapDek(rsaPubKey, perItemDek);
                const org_wrapped_dek = bufferToBase64(new Uint8Array(orgWrappedBuf));

                const migratedItem: MigrateEnvelopeItem = {
                    item_id: item.item_id,
                    item_type: item.item_type,
                    encrypted_fields: reEncryptedFields,
                    user_wrapped_dek,
                    user_dek_iv,
                    org_wrapped_dek,
                    key_version: keyVersion,
                };

                // Compute HMAC search indexes for searchable items
                if (item.item_type !== "summary") {
                    try {
                        const decrypted = await decryptSearchableFields(
                            item.encrypted_fields, rawUserKey, item.item_type,
                        );
                        if (Object.keys(decrypted).length > 0) {
                            migratedItem.hmacs = await encryptNgramsDeterministic(
                                rawUserKey, decrypted,
                            );
                        }
                    } catch (hmacErr) {
                        console.warn(
                            `[useLegacyMigrationWs] HMAC generation failed for ${item.item_type} ${item.item_id}:`,
                            hmacErr,
                        );
                    }
                }

                accumulatedRef.current.push(migratedItem);
                processedIdsRef.current.add(item.item_id);
                ok++;
            } catch (err) {
                console.error(`[useLegacyMigrationWs] Failed ${item.item_type} ${item.item_id}:`, err);
                fail++;
            }
        }
        return { ok, fail };
    }, []);

    const sendNext = useCallback(() => {
        wsRef.current?.send(JSON.stringify({ type: "next" }));
    }, []);

    const sendDone = useCallback(() => {
        wsRef.current?.send(JSON.stringify({ type: "done" }));
    }, []);

    /** Submit all accumulated items atomically, then signal done. */
    const submitAll = useCallback(async () => {
        const items = accumulatedRef.current;
        if (items.length === 0) {
            sendDone();
            return true;
        }

        // Submit in chunks of 10 to avoid request body size limits
        const CHUNK = 10;
        for (let i = 0; i < items.length; i += CHUNK) {
            const chunk = items.slice(i, i + CHUNK);
            try {
                await migrateEnvelope({ items: chunk });
            } catch (err) {
                console.error("[useLegacyMigrationWs] Atomic submit failed:", err);
                return false;
            }
        }

        sendDone();
        return true;
    }, [sendDone]);

    const handleMessage = useCallback(async (event: MessageEvent) => {
        try {
            const data = JSON.parse(event.data);

            if (data.type === "error") {
                console.error("[useLegacyMigrationWs] Server error:", data.message);
                setProgress(prev => ({ ...prev, isRunning: false }));
                return;
            }

            if (data.type === "items") {
                setProgress(prev => ({ ...prev, total: data.total_count }));

                if (data.items.length === 0 && !data.has_more) {
                    const success = await submitAll();
                    if (success) {
                        setIsComplete(true);
                        setProgress(prev => ({ ...prev, isRunning: false }));
                    }
                    return;
                }

                const { ok, fail } = await processPage(data.items);

                setProgress(prev => ({
                    ...prev,
                    processed: prev.processed + ok,
                    failed: prev.failed + fail,
                }));

                if (data.has_more && !abortRef.current) {
                    sendNext();
                } else {
                    // All pages received — submit atomically
                    const success = await submitAll();
                    if (success) {
                        setIsComplete(true);
                        setProgress(prev => ({ ...prev, isRunning: false }));
                    } else {
                        setProgress(prev => ({ ...prev, isRunning: false }));
                    }
                }
            }

            if (data.type === "complete") {
                setProgress(prev => ({ ...prev, isRunning: false }));
                setIsComplete(true);
            }
        } catch {
            console.warn("[useLegacyMigrationWs] Failed to parse WS message");
        }
    }, [processPage, sendNext, submitAll]);

    // Stable ref for handleMessage — allows ws.onmessage to always call
    // the latest version without `start` depending on `handleMessage`.
    const handleMessageRef = useRef(handleMessage);
    handleMessageRef.current = handleMessage;

    /** Start the migration. Derives keys, opens WS, begins processing. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const start = useCallback(async (userId: string) => {
        if (wsRef.current) return;

        const currentOrgPublicKey = orgPublicKeyRef.current;
        const currentSecretKey = secretKeyRef.current;
        if (!currentOrgPublicKey || !currentSecretKey) return;

        abortRef.current = false;
        accumulatedRef.current = [];
        processedIdsRef.current.clear();
        setProgress({ total: 0, processed: 0, failed: 0, isRunning: true });
        setIsComplete(false);

        // Derive keys
        const userDek = base64ToUint8Array(currentSecretKey);
        rawUserKeyRef.current = userDek.buffer.slice(
            userDek.byteOffset, userDek.byteOffset + userDek.byteLength,
        ) as ArrayBuffer;
        rsaPubKeyRef.current = await importRsaPublicKey(currentOrgPublicKey.public_key);
        keyVersionRef.current = currentOrgPublicKey.key_version;

        // Open WS
        const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
        const isDev = import.meta.env.MODE === "development";
        const baseUrl = isDev
            ? import.meta.env.VITE_REPORTS_API_URL?.replace(/^https?:/, protocol) ?? `${protocol}//localhost:8000`
            : `${protocol}//${window.location.host}`;
        const wsUrl = `${baseUrl}/reports/ws/legacy-migration/${userId}`;

        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        ws.onmessage = (event) => handleMessageRef.current(event);
        ws.onclose = () => { wsRef.current = null; };
        ws.onerror = () => {
            console.error("[useLegacyMigrationWs] WebSocket error");
            setProgress(prev => ({ ...prev, isRunning: false }));
        };
    }, []);

    const abort = useCallback(() => {
        abortRef.current = true;
        accumulatedRef.current = [];
        wsRef.current?.close(1000);
        setProgress(prev => ({ ...prev, isRunning: false }));
    }, []);

    useEffect(() => {
        return () => { wsRef.current?.close(1000); };
    }, []);

    return { start, abort, progress, isComplete, isReady };
}
